import React, {useEffect, useState} from "react"
import {AsyncResultModal} from "../../AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../../hooks/useAsyncResultIdle"
import {useAccessToken} from "../../../services/auth"
import {Card, CardStatus, isBusinessCard, isCreditCard, isIndividualCard, isVirtualCard} from "../../../resources/card"
import {BusinessCustomer, IndividualCustomer} from "../../../resources/customer"
import {CardActionType, sendLinkForCardAction} from "../../../resources/sendLink"
import {v4 as uuidv4} from "uuid"
import {ErrorDocument, Phone} from "../../../resources/common"
import Admonition from "../../Admonitions/Admonition"

type ContactKey = "email" | "phone"
type ContactInfo<T extends ContactKey> = T extends "email" ? string : Phone

enum RecipientType {
	email = "email",
	sms = "sms",
}

enum RecipientKey {
	email = "email",
	phone = "phone",
}

export type Recipient = {
	type: RecipientType.sms | RecipientType.email
	key: RecipientKey.phone | RecipientKey.email
	value: string | Phone
}

interface Props {
	card: Card
	close: () => void
	onSuccess: () => void
	customer: IndividualCustomer | BusinessCustomer
}

function refreshAndClose(refresh: () => void, close: () => void) {
	return function () {
		refresh()
		close()
	}
}

export function CustomerOutreachModal({card, close, onSuccess, customer}: Props) {
	const accessToken = useAccessToken()
	const [state, sendLink] = useAsyncResultIdle(sendLinkForCardAction)
	const cardStatus = card.attributes.status
	const [showFraudWarning, setShowFraudWarning] = useState(false)

	const getContactInfo = <T extends ContactKey>(key: T): ContactInfo<T> => {
		if (isBusinessCard(card) && customer?.type === "businessCustomer") {
			return card.attributes[key] as ContactInfo<T>
		} else if (isIndividualCard(card) && customer.type === "individualCustomer") {
			return customer.attributes[key] as ContactInfo<T>
		}
		return (key === RecipientKey.email ? "" : {value: ""}) as ContactInfo<T>
	}

	const getContactEmail = () => {
		if (isBusinessCard(card) && customer?.type === "businessCustomer") {
			return customer.attributes.contact.email
		}
	}

	const businessContactEmail = getContactEmail()

	const email = getContactInfo(RecipientKey.email)
	const phone = getContactInfo(RecipientKey.phone)

	const [selectedCardActionType, setSelectedCardActionType] = useState<CardActionType | "">("")
	const [isCustomerSmsConsent, setIsCustomerSmsConsent] = useState(false)
	const [recipient, setRecipient] = useState<Recipient>({
		type: RecipientType.email,
		key: RecipientKey.email,
		value: email,
	})

	useEffect(() => {
		if (selectedCardActionType === CardActionType.Reactivate) {
			setShowFraudWarning(true)
		}
	}, [selectedCardActionType])

	const request = {
		accessToken,
		idempotencyKey: uuidv4(),
		recipient,
		cardActionType: selectedCardActionType as CardActionType,
		cardId: card.id,
	}

	const isButtonDisabled = () => {
		if (!selectedCardActionType) {
			return true
		}
		return !((recipient.type === RecipientType.sms && isCustomerSmsConsent) || recipient.type === RecipientType.email)
	}

	const handleRecipientChange = (type: Recipient["type"]) => {
		if (type === RecipientType.sms) {
			setRecipient({type: RecipientType.sms, key: RecipientKey.phone, value: phone})
		} else {
			setRecipient({type: RecipientType.email, key: RecipientKey.email, value: email})
			if (isCustomerSmsConsent) {
				setIsCustomerSmsConsent(false)
			}
		}
	}

	/* TODO: This section will be re-enabled after we activate SMS feature */
	// const handleCustomerSmsConsent = () => {
	// 	setIsCustomerSmsConsent(!isCustomerSmsConsent)
	// 	if (recipient.type === RecipientType.email) {
	// 		setRecipient({type: RecipientType.sms, key: RecipientKey.phone, value: phone})
	// 	}
	// }

	const getErrorMessage = (err: ErrorDocument) => {
		if (err.errors[0].code === "action_is_not_supported") {
			return "This action is not supported. Please contact support for more information."
		}
		return err.errors[0].title
	}

	return (
		<AsyncResultModal
			classname="customer-outreach-modal"
			title="Send Customer Outreach"
			close={refreshAndClose(onSuccess, close)}
			buttonClassname="is-black"
			state={state}
			buttonText="Send Outreach"
			successText="Sent"
			onSubmit={() => sendLink(request)}
			errorToText={(err) => getErrorMessage(err)}
			buttonDisabled={isButtonDisabled()}
		>
			<div className="field">
				<label className="label-secondary">Choose which flow to send the customer:</label>

				<div className="select is-fullwidth">
					<select
						value={selectedCardActionType}
						onChange={(e) => {
							setSelectedCardActionType(e.target.value as CardActionType)
						}}
					>
						<option value="" disabled>
							Select
						</option>
						{cardStatus === CardStatus.active && !isCreditCard(card) && (
							<option value={CardActionType.ManagePin}>Manage PIN</option>
						)}
						{cardStatus === CardStatus.active && <option value={CardActionType.Freeze}>Freeze Card</option>}
						{cardStatus === CardStatus.frozen && <option value={CardActionType.Unfreeze}>Unfreeze Card</option>}
						{cardStatus !== CardStatus.frozen && cardStatus !== CardStatus.suspectedFraud && !isVirtualCard(card) && (
							<option value={CardActionType.Replace}>Replace Card</option>
						)}
						{(cardStatus === CardStatus.active || cardStatus === CardStatus.frozen) && (
							<option value={CardActionType.Report}>Report Card</option>
						)}
						{(cardStatus === CardStatus.active ||
							cardStatus === CardStatus.frozen ||
							cardStatus === CardStatus.inactive) && <option value={CardActionType.Close}>Close Card</option>}
						{(cardStatus === CardStatus.inactive || cardStatus === CardStatus.activeForOnlineUse) && (
							<option value={CardActionType.Activate}>Activate Card</option>
						)}
						{cardStatus === CardStatus.suspectedFraud && (
							<option value={CardActionType.Reactivate}>Reactivate Card</option>
						)}
					</select>
				</div>
				{showFraudWarning && (
					<Admonition
						type="is-danger"
						title="Warning"
						message="The card has been flagged for fraud. If the card has been compromised, reactivating it may expose you to additional losses."
					/>
				)}
			</div>
			<div className="field">
				<div className="select-box-wrapper">
					<div className="select-box">
						<label className="radio">
							<input
								type="radio"
								name="email"
								checked={recipient.type === RecipientType.email}
								onChange={() => handleRecipientChange(RecipientType.email)}
							/>
							Send an Email
						</label>
						<div className="text">The outreach will be sent to this email address:</div>
						{businessContactEmail ? (
							<div className="select is-fullwidth">
								<select
									value={recipient.value as string}
									onChange={(e) => {
										setRecipient({type: RecipientType.email, key: RecipientKey.email, value: e.target.value})
									}}
								>
									<option value={email}>{email}</option>
									<option value={businessContactEmail}>{businessContactEmail}</option>
								</select>
							</div>
						) : (
							<div className="highlight-text">{email}</div>
						)}
					</div>
					{/* TODO: This section will be re-enabled after we activate SMS feature */}
					{/*<div className="select-box">*/}
					{/*	<label className="radio">*/}
					{/*		<input*/}
					{/*			type="radio"*/}
					{/*			name="sms"*/}
					{/*			checked={recipient.type === RecipientType.sms}*/}
					{/*			onChange={() => handleRecipientChange(RecipientType.sms)}*/}
					{/*		/>*/}
					{/*		Send an SMS*/}
					{/*	</label>*/}
					{/*	<div className="highlight-text">{`+${phone?.countryCode} ${phone?.number}`}</div>*/}
					{/*	<label className="checkbox">*/}
					{/*		<input*/}
					{/*			type="checkbox"*/}
					{/*			name="sms-confirm"*/}
					{/*			checked={isCustomerSmsConsent}*/}
					{/*			onChange={handleCustomerSmsConsent}*/}
					{/*		/>*/}
					{/*		I confirm the customer requested outreach via SMS*/}
					{/*	</label>*/}
					{/*</div>*/}
				</div>
			</div>
		</AsyncResultModal>
	)
}
